<script lang="ts">
    import "../css/player-theme.css";
    import { url } from "../stores/stream";
    import VideoSlider from "./VideoSlider.svelte";
    import { currentSegmentIdx } from "../stores/segment";
    import {
        VimePlayer,
        VimeUi,
        VimeVideo,
        VimeControls,
        VimeControlGroup,
        VimeControlSpacer,
        VimePlaybackControl,
        VimeSettingsControl,
    } from "@vime/svelte";
    import {
        currentTime,
        duration,
        ready,
        playing,
        paused,
        seeking,
    } from "../stores/player";

    import { segments } from "../constants/segments";
    import VideoSettings from "./VideoSettings.svelte";

    $ready = false;
    const onCurrentTimeChange = (event: CustomEvent<number>) => {
        $currentTime = event.detail;
    };

    const onDurationChange = (event: CustomEvent<number>) => {
        $duration = event.detail;
    };

    const onErrorsChange = (event: CustomEvent<any[]>) => {
        if (event.detail) {
            event.detail.forEach((e) => console.error(e));
        }
    };

    const onPausedChanged = (event: CustomEvent<boolean>) => {
        $paused = event.detail;
    };

    const onPlayingChange = (event: CustomEvent<boolean>) => {
        $playing = event.detail;
    };

    const onSeekingChange = (event: CustomEvent<boolean>) => {
        $seeking = event.detail;
    };

    const onReady = () => {
        $currentTime = segments[$currentSegmentIdx].start;
        $ready = true;
    };

    const onKeydown = (event: KeyboardEvent) => {
        if (event.key === " " || event.key === "k") {
            event.preventDefault();
            if ($currentTime < segments[$currentSegmentIdx].end)
                $paused = !$paused;
        } else if (event.key === "ArrowLeft" || event.key === "j") {
            event.preventDefault();
            $currentTime = segments[$currentSegmentIdx].start;
        } else if (event.key === "l") {
            event.preventDefault();
            $currentTime = $currentTime + 0.01;
        }
    };

    $: if ($ready && !$seeking && $playing) {
        if ($currentTime >= segments[$currentSegmentIdx].end) {
            // pause video at the end of the current segment
            $paused = true;
            $currentTime = segments[$currentSegmentIdx].start;
        } else if ($currentTime < segments[$currentSegmentIdx].start - 2) {
            // some browsers don't support seeking to the beginning of the segment on onReady, re-do it here
            $currentTime = segments[$currentSegmentIdx].start;
            $paused = false;
        }
    }
</script>

<svelte:window on:keydown={onKeydown} />

<div data-sprite="images/icons/sprite.svg" style="display: none;">
    <svg>
        <defs>
            <clipPath id="clip0">
                <path d="M0 0h18v18H0z" />
            </clipPath>
            <clipPath id="clip0">
                <path d="M0 0h18v18H0z" />
            </clipPath>
            <clipPath id="clip0">
                <path d="M0 0h18v18H0z" />
            </clipPath>
            <clipPath id="clip0">
                <path d="M0 0h18v18H0z" />
            </clipPath>
        </defs>
        <symbol id="vime-checkmark" viewBox="0 0 12 12">
            <path
                d="M1.50693 5.42938L0 6.9363L3.86703 10.8255L12 2.69254L10.482 1.17453L3.86703 7.78949L1.50693 5.42938Z" />
        </symbol>
        <symbol id="vime-enter-fullscreen">
            <path
                d="M2 1a1 1 0 00-1 1v5h2.286V3.286H7V1H2zM11 1v2.286h3.714V7H17V2a1 1 0 00-1-1h-5zM17 11h-2.286v3.714H11V17h5a1 1 0 001-1v-5zM3.286 14.714H7V17H2a1 1 0 01-1-1v-5h2.286v3.714z" />
        </symbol>
        <symbol id="vime-pause">
            <path d="M3.5 1h4v16h-4V1zM10.5 1h4v16h-4V1z" />
        </symbol>
        <symbol id="vime-play">
            <path
                d="M3 1.52c0-.398.644-.637.977-.418L15 8.662a.5.5 0 010 .835L3.977 16.911c-.333.219-.977-.02-.977-.418V1.52z" />
        </symbol>
        <symbol id="vime-settings">
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8.072 1a.1.1 0 00-.095.068l-.73 2.192a5.963 5.963 0 00-1.065.442L4.115 2.668a.1.1 0 00-.116.019L2.687 3.999a.1.1 0 00-.019.116l1.034 2.067c-.18.337-.328.693-.442 1.065l-2.192.73A.1.1 0 001 8.072v1.856a.1.1 0 00.068.095l2.192.73c.114.372.262.728.442 1.065l-1.034 2.067a.1.1 0 00.019.116l1.312 1.312a.1.1 0 00.116.019l2.067-1.034c.337.18.693.328 1.065.442l.73 2.192a.1.1 0 00.095.068h1.856a.1.1 0 00.095-.068l.73-2.192a5.956 5.956 0 001.065-.442l2.067 1.034a.1.1 0 00.116-.019l1.312-1.312a.1.1 0 00.019-.116l-1.034-2.067c.18-.337.328-.693.442-1.065l2.192-.73A.1.1 0 0017 9.928V8.072a.1.1 0 00-.068-.095l-2.192-.73a5.956 5.956 0 00-.442-1.065l1.034-2.067a.1.1 0 00-.019-.116l-1.312-1.312a.1.1 0 00-.116-.019l-2.067 1.034a5.962 5.962 0 00-1.065-.442l-.73-2.192A.1.1 0 009.928 1H8.072zM9 12a3 3 0 100-6 3 3 0 000 6z" />
        </symbol>
    </svg>
</div>

<VimePlayer
    currentTime={$currentTime}
    paused={$paused}
    on:vCurrentTimeChange={onCurrentTimeChange}
    on:vDurationChange={onDurationChange}
    on:vErrorsChange={onErrorsChange}
    on:vPausedChange={onPausedChanged}
    on:vReady={onReady}
    on:vPlayingChange={onPlayingChange}
    on:vSeekingChange={onSeekingChange}
    muted
    playsinline={true}
    autoplay={false}
    autopause={false}>
    <VimeVideo poster="/images/poster.png">
        <source src={$url} type="video/mp4" />
    </VimeVideo>
    <VimeUi>
        <VimeControls fullWidth>
            <VideoSlider />
            <VimeControlGroup>
                <VimePlaybackControl />
                <VimeControlSpacer />
                <VimeSettingsControl />
            </VimeControlGroup>
        </VimeControls>
        <VideoSettings />
    </VimeUi>
</VimePlayer>
